import React, { useState } from 'react';
import { TransactionCategory } from '../types';
import { api } from '../services/api';
import { X, Tag, Plus, Trash2, Loader2, ArrowUpRight, ArrowDownLeft, TrendingUp, TrendingDown } from 'lucide-react';

interface CategoryManagerDialogProps {
    categories: TransactionCategory[];
    setCategories: React.Dispatch<React.SetStateAction<TransactionCategory[]>>;
    onClose: () => void;
}

const CategoryManagerDialog: React.FC<CategoryManagerDialogProps> = ({ categories, setCategories, onClose }) => {
    const [newCategoryName, setNewCategoryName] = useState('');
    const [newCategoryType, setNewCategoryType] = useState<'Expense' | 'Income'>('Expense');
    const [isAddingCategory, setIsAddingCategory] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const handleAddCategory = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newCategoryName.trim()) return;
        setIsAddingCategory(true);
        try {
            const added = await api.transactionCategories.create({ name: newCategoryName, type: newCategoryType });
            setCategories([...categories, added]);
            setNewCategoryName('');
            alert("✅ تم إضافة التصنيف بنجاح");
        } catch (error: any) {
            alert("❌ تعذر إضافة التصنيف: " + error.message);
        } finally {
            setIsAddingCategory(false);
        }
    };

    const handleDeleteCategory = async (id: string, name: string) => {
        if (!await window.confirm(`هل أنت متأكد من حذف تصنيف: ${name}؟`)) return;
        setDeletingId(id);
        try {
            await api.transactionCategories.delete(id);
            setCategories(categories.filter(c => String(c.id) !== String(id)));
            alert("✅ تم حذف التصنيف بنجاح");
        } catch (error: any) {
            const errorMsg = error.message || '';
            if (errorMsg.includes('مستخدم في معاملات') || errorMsg.includes('referenced') || errorMsg.includes('Delete failed') || errorMsg.includes('400')) {
                alert("⚠️ تنبيه هام:\nلا يمكن حذف هذا التصنيف لأنه مرتبط بمعاملات مالية (إيرادات أو مصروفات) مسجلة بالفعل في النظام.\n\nلحماية سلامة السجلات المالية والتقارير التاريخية، يرجى تعديل تلك المعاملات أولاً أو حذفها قبل محاولة حذف هذا التصنيف.");
            } else {
                alert("❌ تعذر حذف التصنيف: " + errorMsg);
            }
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn overflow-hidden">
            <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh] animate-scaleIn">
                {/* Header */}
                <div className="bg-slate-50 p-6 border-b border-slate-200 flex justify-between items-center shrink-0">
                    <div>
                        <h2 className="text-xl font-black text-slate-800 flex items-center gap-2">
                            <div className="bg-emerald-50 p-2 rounded-xl border border-emerald-100">
                                <Tag size={20} className="text-emerald-600" />
                            </div>
                            <span>إدارة تصنيفات الإيرادات والمصروفات</span>
                        </h2>
                        <p className="text-xs text-slate-400 mt-1">تخصيص بنود التصنيف المناسبة للحركات والسندات المالية في النظام</p>
                    </div>
                    <button onClick={onClose} className="p-2 bg-slate-50 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all border border-slate-100">
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar bg-slate-50/50">
                    {/* Add Category Form */}
                    <form onSubmit={handleAddCategory} className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm space-y-4">
                        <h3 className="font-bold text-slate-800 text-sm">إضافة تصنيف جديد</h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                            <div className="space-y-1">
                                <label className="block text-xs font-bold text-slate-500">اسم التصنيف *</label>
                                <input 
                                    type="text" 
                                    value={newCategoryName}
                                    onChange={(e) => setNewCategoryName(e.target.value)}
                                    placeholder="مثال: رواتب، صيانة..."
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-slate-800 focus:outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all font-bold text-sm shadow-sm"
                                    required
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="block text-xs font-bold text-slate-500">النوع *</label>
                                <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200/50">
                                    <button 
                                        type="button"
                                        onClick={() => setNewCategoryType('Expense')}
                                        className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                            newCategoryType === 'Expense' ? 'bg-red-600 text-white shadow-md' : 'text-slate-500 hover:text-slate-700'
                                        }`}
                                    >
                                        مصروف
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={() => setNewCategoryType('Income')}
                                        className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                            newCategoryType === 'Income' ? 'bg-emerald-600 text-white shadow-md' : 'text-slate-500 hover:text-slate-700'
                                        }`}
                                    >
                                        إيراد
                                    </button>
                                </div>
                            </div>

                            <button 
                                type="submit" 
                                disabled={isAddingCategory || !newCategoryName.trim()}
                                className={`w-full py-2.5 rounded-xl font-bold flex items-center justify-center gap-2 transition-all disabled:opacity-50 text-sm shadow-md text-white ${
                                    newCategoryType === 'Income' 
                                    ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/15' 
                                    : 'bg-red-600 hover:bg-red-700 shadow-red-600/15'
                                }`}
                            >
                                {isAddingCategory ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                                <span>إضافة</span>
                            </button>
                        </div>
                    </form>

                    {/* Lists Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Incomes List */}
                        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm space-y-4">
                            <h4 className="font-black text-emerald-700 border-b border-slate-100 pb-3 text-xs flex items-center gap-2">
                                <div className="bg-emerald-50 p-1.5 rounded-lg text-emerald-600 border border-emerald-100">
                                    <TrendingUp size={14} />
                                </div>
                                <span>تصنيفات الإيرادات المسجلة</span>
                                <span className="mr-auto bg-emerald-50 text-emerald-700 border border-emerald-100 px-2 py-0.5 rounded-md text-[10px]">
                                    {categories.filter(c => c.type === 'Income').length} بند
                                </span>
                            </h4>
                            <div className="space-y-2 max-h-[280px] overflow-y-auto custom-scrollbar pr-1">
                                {categories.filter(c => c.type === 'Income').map(cat => (
                                    <div key={cat.id} className="bg-emerald-50/30 border border-emerald-100 p-2.5 rounded-xl flex justify-between items-center text-xs hover:border-emerald-300 hover:bg-emerald-50/50 transition-all duration-200 group">
                                        <div className="flex items-center gap-2">
                                            <div className="bg-emerald-100 text-emerald-700 p-1.5 rounded-lg">
                                                <ArrowUpRight size={12} className="font-bold" />
                                            </div>
                                            <span className="font-bold text-slate-700 text-[13px]">{cat.name}</span>
                                        </div>
                                        <button 
                                            onClick={() => handleDeleteCategory(cat.id, cat.name)} 
                                            disabled={deletingId === cat.id}
                                            className="text-slate-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-lg transition-all border border-transparent hover:border-red-100 opacity-80 group-hover:opacity-100"
                                        >
                                            {deletingId === cat.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                                        </button>
                                    </div>
                                ))}
                                {categories.filter(c => c.type === 'Income').length === 0 && (
                                    <div className="text-center py-8 text-slate-400 italic text-xs">لا توجد تصنيفات إيراد مضافة.</div>
                                )}
                            </div>
                        </div>
                        
                        {/* Expenses List */}
                        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm space-y-4">
                            <h4 className="font-black text-red-700 border-b border-slate-100 pb-3 text-xs flex items-center gap-2">
                                <div className="bg-red-50 p-1.5 rounded-lg text-red-600 border border-red-100">
                                    <TrendingDown size={14} />
                                </div>
                                <span>تصنيفات المصروفات المسجلة</span>
                                <span className="mr-auto bg-red-50 text-red-700 border border-red-100 px-2 py-0.5 rounded-md text-[10px]">
                                    {categories.filter(c => c.type === 'Expense').length} بند
                                </span>
                            </h4>
                            <div className="space-y-2 max-h-[280px] overflow-y-auto custom-scrollbar pr-1">
                                {categories.filter(c => c.type === 'Expense').map(cat => (
                                    <div key={cat.id} className="bg-rose-50/20 border border-rose-100/70 p-2.5 rounded-xl flex justify-between items-center text-xs hover:border-rose-300 hover:bg-rose-50/40 transition-all duration-200 group">
                                        <div className="flex items-center gap-2">
                                            <div className="bg-rose-100 text-red-700 p-1.5 rounded-lg">
                                                <ArrowDownLeft size={12} className="font-bold" />
                                            </div>
                                            <span className="font-bold text-slate-700 text-[13px]">{cat.name}</span>
                                        </div>
                                        <button 
                                            onClick={() => handleDeleteCategory(cat.id, cat.name)} 
                                            disabled={deletingId === cat.id}
                                            className="text-slate-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-lg transition-all border border-transparent hover:border-red-100 opacity-80 group-hover:opacity-100"
                                        >
                                            {deletingId === cat.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                                        </button>
                                    </div>
                                ))}
                                {categories.filter(c => c.type === 'Expense').length === 0 && (
                                    <div className="text-center py-8 text-slate-400 italic text-xs">لا توجد تصنيفات مصروف مضافة.</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 bg-white border-t border-slate-200 flex justify-center shrink-0">
                    <button onClick={onClose} className="px-12 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-all text-xs font-black">
                        إغلاق النافذة
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CategoryManagerDialog;
